<template>
  <div
    :class="
      side === 'right'
        ? 'flex justify-end items-end' + (showImageModal ? '' : ' space-x-2')
        : 'flex items-start' + (showImageModal ? '' : ' space-x-2')
    "
    class="max-w-full"
  >
    <!-- If bubble is on the left, show avatar on the left -->
    <div
      v-if="side === 'left'"
      class="w-12 h-12 min-w-12 mb-3 rounded-full mr-1 flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden"
    >
      <template v-if="avatar">
        <img :src="avatar" alt="Avatar" class="w-12 h-12 rounded-full" />
      </template>
      <template v-else>
        <i class="fa-solid fa-user text-4xl -mb-[1px] mt-auto"></i>
      </template>
    </div>

    <div
      :class="side === 'right' ? 'text-right' : 'text-left'"
      class="max-w-[75%] flex flex-col"
    >
      <!-- Message Content -->
      <template v-if="image">
        <!-- Loading state during conversion -->
        <div v-if="isConverting" class="w-full h-34 rounded-t-2xl max-w-xs bg-gray-200 flex items-center justify-center">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span class="ml-2 text-sm">Converting image...</span>
        </div>
        
        <!-- Conversion error state -->
        <div v-else-if="conversionError" class="w-full h-34 rounded-t-2xl max-w-xs bg-red-100 flex items-center justify-center">
          <i class="fa-solid fa-exclamation-triangle text-red-500"></i>
          <span class="ml-2 text-sm text-red-700">Conversion failed</span>
        </div>
        
        <!-- Successfully converted or supported image -->
        <img
          v-else-if="!image.endsWith('.heic')"
          :src="convertedImage || image"
          alt="Image"
          class="w-full h-34 rounded-t-2xl max-w-xs cursor-pointer"
          @click="openImageModal(convertedImage || image)"
        />
        <video
          v-else
          :src="image"
          class="w-full h-34 rounded-t-2xl max-w-xs cursor-pointer"
          @click="openImageModal(image)"
        ></video>
      </template>
      <div v-if="files && files.length > 0" class="flex flex-col gap-2">
        <div
          v-for="file in files"
          :key="file.name"
          class="bg-eerie-black rounded-lg p-2 flex justify-between items-start gap-2 min-w-[200px]"
        >
          <div class="flex flex-col overflow-hidden whitespace-nowrap">
            <p
              class="text-sm text-left truncate text-total-white"
              :title="file.file_name"
            >
              <i class="fa-solid fa-file-alt mr-2"></i>
              {{ file.file_name }}
            </p>
            <p class="text-xs text-total-white-700">
              {{ getFriendlyFileType(file.file_type, file.file_name) }}
            </p>
          </div>
          <a
            :href="file.file_url"
            target="_blank"
            class="text-prime-golden flex-shrink-0"
          >
            <img src="@/assets/images/download.svg" class="w-[18px]" />
          </a>
        </div>
      </div>
      <div
        :class="[
          side === 'right'
            ? 'bg-silver-grey rounded-br-none text-black ml-auto'
            : 'bg-charcoal-grey rounded-bl-none text-total-white',
          image || (files && files.length > 0) ? 'rounded-b-2xl w-full' : 'rounded-2xl',
        ]"
        class="block max-w-full"
      >
        <p class="text-sm p-2 px-3 text-left leading-5 font-medium break-words">
          {{ message }}
        </p>
      </div>
      <p
        :class="[
          side === 'right'
            ? 'text-right text-total-white-500'
            : 'text-left text-total-white-500',
          'text-xs pt-1',
        ]"
      >
        {{ parseDate(timestamp) }}
      </p>
    </div>

    <!-- If bubble is on the right, show avatar on the right -->
    <div
      v-if="side === 'right'"
      class="w-12 h-12 min-w-12 mb-3 rounded-full ml-1 flex items-center justify-center bg-light-grey-translucent-dark border border-light-grey-translucent-dark overflow-hidden"
    >
      <template v-if="avatar">
        <img :src="avatar" alt="Avatar" class="w-12 h-12 rounded-full" />
      </template>
      <template v-else>
        <i class="fa-solid fa-user text-4xl"></i>
      </template>
    </div>

    <!-- Image Modal -->
    <div
      v-if="showImageModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-lg max-h-[100dvh] max-w-[100dvw]"
    >
      <div class="relative w-[100dvw] h-[100dvh] flex items-center justify-center">
        <button
          @click="showImageModal = false"
          class="fixed top-20 right-4 text-white hover:text-gray-300 z-10"
        >
          <i class="fa-solid fa-times text-2xl"></i>
        </button>
        <img
          v-if="!image.endsWith('.heic')"
          :src="convertedImage || image"
          alt="Image"
          class="max-w-[100vw] max-h-[100vh] object-contain z-2"
          @wheel="handleZoom"
          :style="{
            transform: `scale(${zoomLevel})`,
            position: 'absolute',
            top: imagePosition.y + 'px',
            left: imagePosition.x + 'px',
          }"
          @touchstart="startDrag"
          @touchmove="dragImage"
          @touchend="endDrag"
        />
        <video
          v-else
          :src="image"
          class="max-w-[100vw] max-h-[100vh] object-contain z-2"
          @wheel="handleZoom"
          :style="{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }"
          @touchstart="startDrag"
          @touchmove="dragImage"
          @touchend="endDrag"
        ></video>
      </div>
    </div>
  </div>
</template>

<script setup>
import { parseDate } from "@/misc/util";
</script>

<script>
export default {
  props: {
    side: {
      type: String,
      required: true, // "left" or "right"
    },
    avatar: {
      type: String,
      default: null,
    },
    message: {
      type: String,
      required: true,
    },
    timestamp: {
      type: String,
      required: true,
    },
    image: {
      type: String,
      default: null,
    },
    files: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showImageModal: false,
      zoomLevel: 1,
      imagePosition: { x: 0, y: 0 },
      isDragging: false,
      startPosition: { x: 0, y: 0 },
      // Optionally, if needed, currentImage for modal (or use image prop)
      currentImage: null,
      isConverting: false,
      conversionError: false,
      convertedImage: null,
    };
  },
  methods: {
    getFriendlyFileType(mime, name) {
      if (mime === "application/pdf") return "PDF";
      if (
        mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        mime === "application/vnd.ms-excel"
      )
        return "XLSX";
      // fall back to extension from filename
      const ext = name.split(".").pop().toUpperCase();
      return ext.length <= 5 ? ext : "FILE";
    },
    needsConversion(imageUrl) {
      const unsupportedFormats = ['.webp', '.avif', '.tiff', '.bmp'];
      return unsupportedFormats.some(format => 
        imageUrl.toLowerCase().includes(format)
      );
    },
    async convertImageToJPG(imageUrl) {
      this.isConverting = true;
      this.conversionError = false;
      
      try {
        // Create image element
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Handle CORS if needed
        
        // Wait for image to load
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = imageUrl;
        });
        
        // Create canvas and context
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas dimensions to match image
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to blob
        return new Promise((resolve) => {
          canvas.toBlob((blob) => {
            const convertedUrl = URL.createObjectURL(blob);
            this.convertedImage = convertedUrl;
            resolve(convertedUrl);
          }, 'image/jpeg', 0.9); // 90% quality JPG
        });
        
      } catch (error) {
        console.error('Image conversion failed:', error);
        this.conversionError = true;
        return imageUrl; // Fallback to original
      } finally {
        this.isConverting = false;
      }
    },
    handleZoom(event) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomLevel = Math.min(this.zoomLevel + 0.1, 3);
      } else {
        this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.5);
      }
    },
    formatFileSize(size) {
      return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    },
    startDrag(event) {
      this.isDragging = true;
      this.startPosition = { x: event.touches[0].clientX, y: event.touches[0].clientY };
    },
    dragImage(event) {
      if (this.isDragging) {
        const dx = event.touches[0].clientX - this.startPosition.x;
        const dy = event.touches[0].clientY - this.startPosition.y;
        this.imagePosition.x += dx;
        this.imagePosition.y += dy;
        this.startPosition = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
    },
    endDrag() {
      this.isDragging = false;
    },
    openImageModal(imageUrl) {
      // Use converted image if available, otherwise original
      const modalImage = this.convertedImage || imageUrl;
      this.currentImage = modalImage;
      this.showImageModal = true;
      const img = new Image();
      img.src = modalImage;
      img.onload = () => {
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const imgWidth = img.width;
        const imgHeight = img.height;
        const heightFactor = imgHeight / imgWidth;
        const widthFactor = imgWidth / windowWidth;
        const effectiveHeight = widthFactor * heightFactor;
        this.imagePosition = {
          x: 0,
          y: (windowHeight - effectiveHeight) / 4,
        };
      };
    },
  },
  async mounted() {
    if (this.image && this.needsConversion(this.image)) {
      await this.convertImageToJPG(this.image);
    }
  },
  beforeUnmount() {
    // Clean up blob URLs to prevent memory leaks
    if (this.convertedImage && this.convertedImage.startsWith('blob:')) {
      URL.revokeObjectURL(this.convertedImage);
    }
  },
  watch: {
    showImageModal(newValue) {
      if (!newValue) {
        this.zoomLevel = 1;
      }
    },
  },
};
</script>

<style scoped>
img {
  transition: transform 0.2s ease-in-out;
}
</style>
